<template>
  <Loader
    :value="promises.fetchOne"
    :message="$t('loading', { resource: $tc('residentialAccount').toLowerCase() }) + '...'"
    class="residential-account-dashboard"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #title>
          <div class="row middle">
            <h1 class="header__title">{{ $metaInfo.title }}</h1>
            <Badge
              class="ml-1"
              :theme="data.status === 'published' ? 'success' : 'error'"
              :value="labels[data.status === 'published' ? 'published' : 'unpublished']"
            />
          </div>
        </template>
        <template #actions>
          <Actions placement="residential-account" :query="{ residentialAccount: id }">
            <template #bottom>
              <ButtonComponent :label="$t('delete')" @click="removeDialog = true" :icon="['fal', 'times']" />
              <ButtonComponent
                :label="
                  $t(
                    `${
                      $path('metadata.settings.complaint.blockReceiving', data)
                        ? 'unblockComplaints'
                        : 'blockComplaints'
                    }`,
                  )
                "
                @click="blockReceivingComplaints"
                :icon="['fal', 'ban']"
              />
            </template>
          </Actions>

          <ButtonComponent
            :label="$t('preview')"
            :icon="['fal', 'eye']"
            tag="router-link"
            target="_blank"
            :to="{ name: 'residential-account', params: { id } }"
          />
          <ButtonComponent
            v-if="item.status === 'draft'"
            :label="$t(item.status === 'draft' ? 'publish' : 'published')"
            :icon="['fal', item.status === 'draft' ? 'arrow-to-right' : 'check']"
            :theme="item.status === 'draft' ? 'secondary' : 'success'"
            confirm
            v-tooltip.bottom="{ content: item.status === 'draft' ? $t('residentialAccountPublishAlert') : null }"
            @click="publish"
          />
          <ButtonComponent
            :label="$t('save')"
            :success-label="$t('saved')"
            :loading-label="$t('saving')"
            :icon="['fal', 'arrow-to-bottom']"
            theme="primary"
            style="margin-right: auto"
            :promise="promises.mutate"
            @click="submit"
          />
        </template>
      </HeaderComponent>
      <div class="residential-account-dashboard__content">
        <FDV v-model="data" data-widget="fdv" />
        <Settings v-model="data" data-widget="settings" />
        <Complaints data-widget="complaints" />
      </div>
    </div>
    <RemoveComponent
      :show.sync="removeDialog"
      :title="$path('reference.name', item)"
      :action="() => remove({ id })"
      @onSuccess="
        $router.push({
          name: 'project-dashboard',
          params: { id: $path('reference.project.id', item) },
        })
      "
      :resource="$tc('residentialAccount')"
    />
  </Loader>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { mapi18n } from '@/i18n'
import { mergeDeepRight } from 'ramda'
import Model from '@/mixins/Model'

import HeaderComponent from '@/components/Header'
import RemoveComponent from '@/components/Remove'
import Widgets from './Widgets'
import Actions from '@/components/Actions'
import Badge from '@/components/Badge'
import tracker from '@/tracking'

export default {
  mixins: [
    Model({
      model: {
        id: null,
        documents: [],
        owners: [],
        status: null,
        metadata: {
          settings: {
            documentUploadConfirm: false,
            showShop: true,
            showShopDocuments: true,
            showResidentialDocuments: true,
            complaint: {
              blockReceiving: false,
              disclaimer: '',
            },
          },
        },
      },
      guard: true,
      transform: {
        owners: v => v && v.map(x => x.id),
      },
    }),
  ],
  props: {
    id: String,
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      removeDialog: false,
      resendPromise: null,
    }
  },
  computed: {
    ...mapState('ResidentialAccount', ['item', 'promises']),
    ...mapi18n('labels', {
      published: 'published',
      unpublished: 'unpublished',
      loading() {
        return this.$tc('loading', { resource: '' }) + '...'
      },
      residentialAccount: 'residentialAccount',
    }),
    breadcrumb() {
      if (!this.item.id) return []
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$path('item.reference.project.name'),
          action: {
            name: 'project-dashboard',
            params: { id: this.$path('item.reference.project.id') },
          },
        },
        {
          title: this.$path('item.reference.name'),
          action: {
            name: 'residential-edit',
            params: { id: this.$path('item.reference.id') },
          },
        },
        { title: this.labels.residentialAccount },
      ]
    },
  },
  methods: {
    ...mapActions('ResidentialAccount', {
      remove(dispatch, payload) {
        return dispatch('remove', { id: this.id }).then(() => this.$emit('dataguard/reset'))
      },
      fetch(dispatch) {
        return dispatch('fetch', [{ id: this.id }, { query: 'ResidentialAccountDashboard' }]).then(() => {
          if (!this.item.id) return this.$router.replace({ name: 'notfound' })
        })
      },
      submit(dispatch) {
        return dispatch('mutate', [
          { id: this.id, ...this.getData({ diff: true }) },
          {
            strategy: item => mergeDeepRight(item, this.getData({ transform: false })),
          },
        ])
      },
    }),
    publish() {
      this.data.status = 'published'
      tracker.emit('publish:mypage', {})
      this.submit()
    },
    blockReceivingComplaints() {
      this.data.metadata.settings.complaint.blockReceiving = !this.data.metadata.settings.complaint.blockReceiving
      this.submit()
    },
  },
  metaInfo() {
    return {
      title: this.item.id
        ? `${this.$path('item.reference.name')} - ${this.labels.residentialAccount}`
        : this.labels.loading,
    }
  },
  components: {
    HeaderComponent,
    RemoveComponent,
    Actions,
    Badge,
    ...Widgets,
  },
}
</script>

<style lang="scss">
.residential-account-dashboard {
  background-color: $background-grey;

  &__content {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem 2rem;

    @include respond-below('tablet') {
      grid-template-columns: 1fr;
    }

    [data-widget='complaints'] {
      grid-column-end: span 2;

      @include respond-below('tablet') {
        grid-column-end: span 1;
      }
    }
  }
}
</style>
